import { Dayjs } from "dayjs";
import { SaleOrder } from "@/types/general";
import { ReservationAllocationSchema, ReservationSchema } from "../../types";

// Kept next to the location picker, which lists locations by the same keys
export { getLocationKey } from "@ready/ReservationLocationCommandDropdown";

export function sumAllocations(allocations: ReservationAllocationSchema[]): number {
    return allocations.reduce((acc, allocation) => acc + allocation.quantity, 0);
}

/**
 * Trims allocations, so their sum doesn't exceed `max`. Quantity is taken away from the most recently added
 * allocations first, and the ones reduced to 0 are dropped.
 */
export function capAllocations(
    allocations: ReservationAllocationSchema[],
    max: number
): ReservationAllocationSchema[] {
    let remaining = max;

    return allocations
        .map((allocation) => {
            const quantity = Math.min(allocation.quantity, Math.max(remaining, 0));
            remaining -= quantity;
            return { ...allocation, quantity };
        })
        .filter((allocation) => allocation.quantity > 0);
}

/**
 * Adds quantity to the global allocation, creating one if the product is currently reserved only per location.
 */
export function addToGlobalAllocation(
    allocations: ReservationAllocationSchema[],
    quantity: number
): ReservationAllocationSchema[] {
    const globalIndex = allocations.findIndex((allocation) => allocation.location === null);

    if (globalIndex === -1) {
        return [...allocations, { location: null, quantity }];
    }

    return allocations.map((allocation, index) =>
        index === globalIndex
            ? { ...allocation, quantity: allocation.quantity + quantity }
            : allocation
    );
}

/**
 * Converts a reservation record from `GET /sale/{id}` into a form allocation.
 */
export function toReservationAllocation(
    reservation: SaleOrder.Reservation
): ReservationAllocationSchema {
    if (reservation.locations === undefined) {
        return { id: reservation.id, location: null, quantity: reservation.quantity };
    }

    // Backend keeps a record per location and always wraps it into a single-element array
    if (reservation.locations.length !== 1) {
        throw new Error(
            `Reservation #${reservation.id} is expected to have exactly one location, received ${reservation.locations.length}`
        );
    }

    const [location] = reservation.locations;

    return {
        id: reservation.id,
        location: { store_id: location.store_id, section_id: location.section_id },
        quantity: location.quantity,
    };
}

/**
 * Flattens form reservations into the mutation payload - one entry per allocation, as the backend
 * updates only the first location of an entry that carries an `id`.
 *
 * @param originalReservations - reservations the order was loaded with, used to detect per-location reservations that
 * the user has just turned into global ones. Omitting `locations` on those would keep the old location, so they are
 * sent with `locations: null` instead of being recreated, which would cost them their seniority for picking.
 */
export function buildReservationsPayload(
    reservations: ReservationSchema[] | undefined,
    originalReservations: SaleOrder.Reservation[] = []
): SaleOrder.DTO.MutationReservation[] | undefined {
    const perLocationReservationIds = new Set(
        originalReservations
            .filter((reservation) => reservation.locations !== undefined)
            .map((reservation) => reservation.id)
    );

    return reservations?.flatMap((reservation) =>
        reservation.allocations.map((allocation) => {
            const base = {
                ...(allocation.id && { id: allocation.id }),
                product_id: reservation.product.id,
            };

            if (allocation.location === null) {
                return {
                    ...base,
                    quantity: allocation.quantity,
                    ...(allocation.id &&
                        perLocationReservationIds.has(allocation.id) && { locations: null }),
                };
            }

            return {
                ...base,
                locations: [
                    {
                        store_id: allocation.location.store_id,
                        section_id: allocation.location.section_id,
                        quantity: allocation.quantity,
                    },
                ],
            };
        })
    );
}

/**
 * Backend validates the expiration as `after:now` and reads a bare date as midnight, so the end of the selected day
 * is sent - otherwise picking today's date is rejected, and reservations expire a day earlier than users expect.
 * `null` is sent explicitly, because an omitted field keeps the previous expiration on update.
 */
export function formatReservationsExpirationDate(date: Dayjs | null | undefined): string | null {
    return date ? date.endOf("day").format("YYYY-MM-DD HH:mm:ss") : null;
}
